// Store
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetSearchInfo } from "store/slice/actions";
import { customAsyncThunk } from "store/slice/slices.functions";

// Store
import { RootState } from "app/store";

// Utils
import { differenceBy } from "lodash";

// Types
import { RequestInfo } from "store/slice/store.types";
import { AllInsightChildren, InsightChildTypes, InsightData } from "services/projects/project.types";
import { fetchProjectByIdService, ProjectRequestOptions } from "services/projects/project.service";

type TInsightsSlice = {
    status: RequestInfo;
    data: InsightData[];
};

const initialState: TInsightsSlice = {
    status: "pristine",
    data: [] as InsightData[],
};

export const getProjectInsight = customAsyncThunk<
    InsightData,
    { projectId: number | string; path: string; options?: ProjectRequestOptions }
>("project/insights/getInsight", async ({ projectId, path, options }, { getState }) => {
    const {
        project: {
            insights: { data: insight },
        },
    } = getState();

    const insightData = insight.find((insight) => insight.meta.path === path);

    if (insightData) {
        return insightData;
    } else {
        const { data } = await fetchProjectByIdService<InsightData>(Number(projectId), path, {
            include_avatars: true,
            ...options,
        });
        return data;
    }
});

const insightsSlice = createSlice({
    name: "project/insights",
    initialState,
    reducers: {
        addInsightsData: (state, action: PayloadAction<InsightData[] | undefined>) => {
            const existingInsights = state.data;
            const newInsights = action.payload || [];
            const newValues = differenceBy(newInsights, existingInsights, "data.id");
            return { ...state, data: [...state.data, ...newValues] };
        },
        updateInsightChild: (
            state,
            action: PayloadAction<{
                data: AllInsightChildren;
                id: number;
                type: InsightChildTypes;
            }>
        ) => {
            const { data, id, type } = action.payload;
            const insight = state.data.find((insight) => insight.data.id === id);
            if (insight) {
                const insightChildren = insight.children.map((insightChild) => {
                    return insightChild.meta.type === type ? data : insightChild;
                });
                const updatedInsight = { ...insight, children: insightChildren };
                const insights = state.data.map((insight) => {
                    return insight.data.id === id ? updatedInsight : insight;
                });

                return { ...state, data: insights };
            }
            return state;
        },
        clearInsightsData: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(getProjectInsight.pending, (state) => ({ ...state, status: "fetching" }));
        builder.addCase(getProjectInsight.rejected, (state) => ({ ...state, status: "error" }));
        builder.addCase(getProjectInsight.fulfilled, (state, action) => ({
            ...state,
            data: state.data.find((d) => d.meta.path === action.payload.meta.path)
                ? state.data
                : [...state.data, action.payload],
            status: "done",
        }));
        builder.addCase(resetSearchInfo, () => initialState);
    },
});

export const { addInsightsData, updateInsightChild, clearInsightsData } = insightsSlice.actions;

export const getInsights = (store: RootState) => store.project.insights;
export const getInsightById = (insightId: number) => (store: RootState) => ({
    data: store.project.insights.data.find((insight) => insight.data.id === Number(insightId)),
    status: store.project.insights.status,
});

export default insightsSlice.reducer;
